import { Component, OnDestroy, OnInit, SecurityContext } from '@angular/core';
import { OfferService } from '../../_services/offer.service';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { IPagination } from '../../_models/pagination';
import { environment } from '../../../environments/environment';
import { CartService } from '../../_services/cart.service';
import { getPrice, isOfferInPromotion, Offer } from '../../_models/offer';
import { User } from '../../_models/user';
import { WishlistService } from 'src/app/_services/wishlist.service';
import { DomSanitizer, SafeHtml, Title } from '@angular/platform-browser';
import { ToastrService } from 'ngx-toastr';
import { StoreService } from 'src/app/_services/store.service';
import { TranslateService } from '@ngx-translate/core';
import { finalize, Subscription } from 'rxjs';
import { TaxonomyService } from 'src/app/_services/taxonomy.service';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { SearchOfferResponse } from 'src/app/_models/search-offer-response';
import { ProductCategoryService } from '../../_services/product-category.service';
import { IProductCategory } from '../../_models/iproduct-category';

@Component({
    selector: 'app-products-list',
    templateUrl: './products-list.component.html',
    styleUrls: ['./products-list.component.css'],
    standalone: false
})
export class ProductsListComponent implements OnInit, OnDestroy {

    title = 'Products';
    titleTranslateKey = 'common.products';

    offers: Offer[];
    recommendedOffers: Offer[] = [];
    loading = false;
    pricing: string = null;
    page: any;
    config: any;
    q: string;
    categorySlug: string;
    isSearch = false;
    user: User = null;
    slug = null;
    taxonomy_id = null;
    subscriptions: Subscription[] = [];

    lang = 'fr';
    showSearch = false;
    searchForm: UntypedFormGroup;

    categories: IProductCategory[] = [];
    selectedCategoryId: number | null = null;
    aggregations: { [id: number]: number } = {};
    fullAggregations: { [id: number]: number } = {};
    minPrice: number | null = null;
    maxPrice: number | null = null;
    sortBy: string = 'relevance';
    priceError: string | null = null;
    filtersExpanded = { price: true, categories: true };

    constructor(private offerService: OfferService,
        private activatedRoute: ActivatedRoute,
        private cartService: CartService,
        private storeService: StoreService,
        private wishlistService: WishlistService,
        private titleService: Title,
        private toastrService: ToastrService,
        private router: Router,
        private translateService: TranslateService,
        private _sanitizer: DomSanitizer,
        private taxonomyService: TaxonomyService,
        private fb: UntypedFormBuilder,
        private productCategoryService: ProductCategoryService
    ) {
        const titleSubscription = this.translateService.get(this.titleTranslateKey).subscribe(value => {
            this.title = value;
            this.titleService.setTitle(this.title);
        });

        this.subscriptions.push(titleSubscription);
        this.user = JSON.parse(localStorage.getItem('currentUser') ?? '{}');
        this.lang = translateService.currentLang;
    }

    ngOnInit() {
        this.searchForm = this.fb.group({ q: [''] });
        this.loading = true;

        const paramMapSubscription = this.activatedRoute.paramMap.subscribe(pm => {
            this.pricing = pm.get('pricing') || null;
            this.slug = pm.get('slug') || null;
            this.taxonomy_id = pm.get('taxonomy_id') || null;

            if (!this.q) {
                this.isSearch = false;
                this.showSearch = false;
                if (this.pricing) {
                    this.getOffers();
                } else if (this.slug) {
                    this.getOfferByCategory();
                } else if (this.taxonomy_id) {
                    this.loadOffersByTaxonomy();
                }
            }
        });
        this.subscriptions.push(paramMapSubscription);

        // Subscribe to query param changes to handle subsequent searches
        const queryParamsSubscription = this.activatedRoute.queryParamMap.subscribe(paramMap => {
            this.q = paramMap.get('q') || null;
            this.page = paramMap.get('page') || '1';
            const categoryIdParam = paramMap.get('categoryId');
            this.selectedCategoryId = categoryIdParam ? Number(categoryIdParam) : null;

            const minPriceParam = paramMap.get('minPrice');
            this.minPrice = minPriceParam ? Number(minPriceParam) : null;

            const maxPriceParam = paramMap.get('maxPrice');
            this.maxPrice = maxPriceParam ? Number(maxPriceParam) : null;

            this.sortBy = paramMap.get('sortBy') || 'relevance';

            if (this.q) {
                this.isSearch = true;
                this.showSearch = true;
                this.searchForm.get('q').setValue(this.q);
                this.loadCategoriesAndSearch();
            } else {
                this.isSearch = false;
                this.showSearch = false;
            }
        });

        this.subscriptions.push(queryParamsSubscription);
    }

    toggleSearch(): void {
        this.showSearch = !this.showSearch;
        if (this.showSearch) {
            setTimeout(() => {
                const input = document.querySelector('.search-input-wrapper input') as HTMLInputElement;
                if (input) input.focus();
            }, 100);
        }
    }

    closeSearch(): void {
        this.showSearch = false;
        this.searchForm.reset();
        if (this.isSearch) {
            this.router.navigate(['products']);
        }
    }

    onSearch(): void {
        const q = this.searchForm.get('q').value.trim();
        if (q) {
            this.router.navigate(['search'], { queryParams: { q } });
        }
    }

    searchForOffers() {
        this.recommendedOffers = [];

        if (this.selectedCategoryId) {
            this.offerService.search(this.q, this.page)
                .subscribe((fullResult: SearchOfferResponse) => {
                    this.fullAggregations = this.normalizeAggregations(fullResult.aggregations);
                    this.aggregations = this.fullAggregations;

                    this.offerService.search(
                        this.q,
                        this.page,
                        this.selectedCategoryId,
                        this.minPrice,
                        this.maxPrice,
                        this.sortBy
                    )
                        .pipe(finalize(() => this.loading = false))
                        .subscribe({
                            next: (filteredResult: SearchOfferResponse) => {
                                this.setConfig(filteredResult);
                                this.titleService.setTitle('Search for ' + this.q + ' | eFarm.cm ');
                                if (this.offers.length === 0) {
                                    this.taxonomyService.getTaxonomyOffers(1, 1)
                                        .subscribe((r: any) => {
                                            this.recommendedOffers = r.data.slice(0, 8);
                                        });
                                }
                            },
                            error: (err) => {}
                        });
                });
        } else {
            this.offerService.search(
                this.q,
                this.page,
                undefined,
                this.minPrice,
                this.maxPrice,
                this.sortBy
            )
                .pipe(finalize(() => this.loading = false))
                .subscribe({
                    next: (result: SearchOfferResponse) => {
                        this.setConfig(result);
                        this.aggregations = this.normalizeAggregations(result.aggregations);
                        this.fullAggregations = this.aggregations;
                        this.titleService.setTitle('Search for ' + this.q + ' | eFarm.cm ');
                        if (this.offers.length === 0) {
                            this.taxonomyService.getTaxonomyOffers(1, 1)
                                .subscribe((r: any) => {
                                    this.recommendedOffers = r.data.slice(0, 8);
                                });
                        }
                    },
                    error: (err) => {}
                });
        }
    }

    loadCategoriesAndSearch() {
        this.productCategoryService.get_product_categories()
            .subscribe((categories) => {
                this.categories = categories;
                this.searchForOffers();
            });
    }

    selectCategory(categoryId: number | null) {
        this.selectedCategoryId = categoryId;
        this.page = '1';
        this.loading = true;
        this.router.navigate(['search'], {
            queryParams: {
                q: this.q,
                categoryId: categoryId ? categoryId : null,
                minPrice: this.minPrice != null ? this.minPrice : null,
                maxPrice: this.maxPrice != null ? this.maxPrice : null,
                sortBy: this.sortBy !== 'relevance' ? this.sortBy : null,
                page: null
            },
            queryParamsHandling: 'merge'
        });
    }

    getCategoryCount(categoryId: number): number {
        return this.fullAggregations[categoryId] || 0;
    }

    applyPriceFilter() {
        if (this.minPrice != null && this.maxPrice != null && this.maxPrice < this.minPrice) {
            this.priceError = 'search.filters.price_max_below_min';
            this.loading = false;
            return;
        }

        if (this.minPrice != null && this.minPrice < 0) {
            this.priceError = 'search.filters.price_negative';
            this.loading = false;
            return;
        }

        if (this.maxPrice != null && this.maxPrice < 0) {
            this.priceError = 'search.filters.price_negative';
            this.loading = false;
            return;
        }

        this.priceError = null;
        this.loading = true;
        this.page = '1';

        this.router.navigate(['search'], {
            queryParams: {
                q: this.q,
                categoryId: this.selectedCategoryId ? this.selectedCategoryId : null,
                minPrice: this.minPrice != null ? this.minPrice : null,
                maxPrice: this.maxPrice != null ? this.maxPrice : null,
                sortBy: this.sortBy !== 'relevance' ? this.sortBy : null,
                page: null
            },
            queryParamsHandling: 'merge'
        });
    }

    clearPriceError() {
        this.priceError = null;
    }

    onSortChange(newSort: string) {
        this.sortBy = newSort;
        this.applyPriceFilter();
    }

    get categoriesWithResults(): IProductCategory[] {
        return this.categories.filter(c => this.fullAggregations[c.id] > 0);
    }

    private normalizeAggregations(aggregations: any): { [id: number]: number } {
        const normalized: { [id: number]: number } = {};
        if (aggregations && typeof aggregations === 'object' && !Array.isArray(aggregations)) {
            Object.keys(aggregations).forEach(key => {
                normalized[Number(key)] = Number(aggregations[key]);
            });
        }
        return normalized;
    }

    getOffers() {
        this.offerService.loadLastProductsByPricing(this.pricing, this.page)
            .subscribe(
                {
                    next: (result: IPagination) => {
                    this.setConfigForCategory(result);
                    this.titleService.setTitle(
                    (result.data.length > 0 ?
                        result.data[0].pricing.name : this.pricing) + '| eFarm.cm '
                    );
                },
                error: (error) => {},
                complete: () => this.loading = false
                }
            );
    }

    getOfferByCategory() {
        this.offerService.loadOffersByCategory(this.slug, this.page)
            .subscribe(
                {
                    next: (result: IPagination) => {
                    this.setConfigForCategory(result);
                    this.titleService.setTitle(
                        (result.data.length > 0 ?
                            result.data[0].product.category.name : this.categorySlug) + ' en vente | eFarm.cm '
                        );
                },
                error: (error) => {},
                complete: () => this.loading = false
                }
            );
    }

    getHTMLText(htmlText): SafeHtml {
        return this._sanitizer.sanitize(SecurityContext.HTML, htmlText).replace(/<[^>]*>/g, '');
    }

    loadOffersByTaxonomy() {
        this.taxonomyService.getTaxonomyOffers(this.taxonomy_id, this.page)
        .subscribe({
           next: (result: IPagination) => {
                this.setConfigForCategory(result);
                this.titleService.setTitle('Taxonomy | eFarm.cm ');
            },
            error: (error) => {},
            complete: () => this.loading = false
        });
    }

    pageChange(page) {
        this.page = page;
        this.loading = true;
        if(this.pricing){
            this.router.navigate(['products', this.pricing], { queryParams: { page: page } });
            this.getOffers();
        } else if(this.slug) {
            this.router.navigate(['products','category', this.slug], { queryParams: { page: page } });
            this.getOfferByCategory();
        } else if(this.taxonomy_id) {
            this.router.navigate(['products','taxonomy', this.taxonomy_id], { queryParams: { page: page } });
            this.loadOffersByTaxonomy();
        } else if(this.isSearch) {
            this.router.navigate(['search'], { queryParams: { q: this.q, page: page } });
            this.searchForOffers();
        }
    }

    isOfferInCart(offer) {
        return this.storeService.findItem(offer.id);
    }

    addToCart(offer) {
        this.cartService.addToLocalCart(offer)
            .subscribe(() => {
                const addCartMessage = this.translateService.get('common.message_for_added_to_cart')
                    .subscribe(message => {
                        this.toastrService.success(message);
                    });

                this.subscriptions.push(addCartMessage);
            });
    }

    // getImageSrc(imagePath: string) {
    //     return imagePath !== '' && imagePath !== null && imagePath.includes('media')
    //         ? environment.image_cdn_url + '/' + imagePath : '/assets/images/default.jpg';
    // }

    getImageSrc(offer: Offer) {
        return environment.image_cdn_url + '/' +
            (offer.defaultPicture.length > 0 ? offer.defaultPicture + '?func=crop&width=252&height=252' : offer.product.picture);
    }

    printImage(product) {
        return this.getImageSrc(product.picture);
    }

    isInPromotion(offer: Offer): boolean {
        return isOfferInPromotion(offer);
    }

    getOfferPrice(offer: Offer): number {
        return getPrice(offer);
    }

    addToWishlist(offerId: number) {
        this.wishlistService.addToWishList(this.user.id, offerId).subscribe();
    }

    stripHtml(html: string): string {
        if (!html) return '';
        return html.replace(/<[^>]*>/g, '').trim();
    }

    private setConfig(result: SearchOfferResponse) {
        this.offers = result.data;
        this.config = {
            currentPage: result.page,
            itemsPerPage: result.size,
            totalItems: result.totalOfItems,
            totalOfPage: result.totalOfPage,
            id: 'listing'
        };
    }

    private setConfigForCategory(result: IPagination) {
        this.offers = result.data;
        this.config = {
            currentPage: result.page,
            itemsPerPage: result.itemPerPage,
            totalItems: result.totalOfItems,
            totalOfPage: result.totalOfPage,
            id: 'listing'
        };
    }

    ngOnDestroy() {
        this.subscriptions.forEach(s => s.unsubscribe());
    }

}
